import { useEffect } from 'react';
import { KeyRecoilProps, TPickDateAction } from '@/shared/model';
import {
  selectControlModal,
  selectIsActiveTab,
  selectPickedBookmarkDate,
  selectPickedEndDate,
  selectPickedStartDate,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';

/**
 * Keeps the HR summary tab's modal and date-picking state consistent as the user switches tabs, strips and modals. Five effects close the control modals when the selected strip changes or the tab goes inactive, advance the pick-date action through START, END, BOOKMARK and NONE as start, end and bookmark dates are chosen, and clear all three dates whenever a modal other than change-duration, add-new-event or add-ECG-bookmark is open.
 *
 * @param keyRecoil - Report store record key identifying which tab's slice of state these effects drive.
 * @returns Nothing; this hook only runs the modal/date-picking effects.
 */
const useTabHRSummaryUIEffect = ({ keyRecoil }: KeyRecoilProps) => {
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Records which end of the date range the user is currently picking. Forwards the value to the report store's setPickDateAction action together with the tab's record key.
   *
   * @param v - The pick-date action to store, such as START, END, BOOKMARK or NONE.
   * @returns Whatever the store's setPickDateAction action returns, normally nothing.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const setPickedStartDateAction = useReportStore((s) => s.actions.setPickedStartDate);
  /**
   * Stores the start of the date range being picked on this tab. Forwards the value to the report store's setPickedStartDate action together with the tab's record key.
   *
   * @param v - The start date to store, or null to clear it.
   * @returns Whatever the store's setPickedStartDate action returns, normally nothing.
   */
  const setPickedStartDate = (v: Parameters<typeof setPickedStartDateAction>[1]) =>
    setPickedStartDateAction(keyRecoil, v);
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const setPickedEndDateAction = useReportStore((s) => s.actions.setPickedEndDate);
  /**
   * Stores the end of the date range being picked on this tab. Forwards the value to the report store's setPickedEndDate action together with the tab's record key.
   *
   * @param v - The end date to store, or null to clear it.
   * @returns Whatever the store's setPickedEndDate action returns, normally nothing.
   */
  const setPickedEndDate = (v: Parameters<typeof setPickedEndDateAction>[1]) =>
    setPickedEndDateAction(keyRecoil, v);
  const pickedBookmarkDate = useReportStore(selectPickedBookmarkDate(keyRecoil));
  const setPickedBookmarkDateAction = useReportStore((s) => s.actions.setPickedBookmarkDate);
  /**
   * Stores the date chosen for a new ECG bookmark on this tab. Forwards the value to the report store's setPickedBookmarkDate action together with the tab's record key.
   *
   * @param v - The bookmark date to store, or null to clear it.
   * @returns Whatever the store's setPickedBookmarkDate action returns, normally nothing.
   */
  const setPickedBookmarkDate = (v: Parameters<typeof setPickedBookmarkDateAction>[1]) =>
    setPickedBookmarkDateAction(keyRecoil, v);
  const controlModal = useReportStore(selectControlModal(keyRecoil));
  const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
  /**
   * Clears the control-modal flags for this tab back to their defaults. Delegates to the report store's resetControlModal action, supplying the tab's record key.
   *
   * @returns Whatever the store's resetControlModal action returns, normally nothing.
   */
  const resetControlModal = () => resetControlModalAction(keyRecoil);
  const resetControlHrSummaryModal = useReportStore((s) => s.actions.resetControlHrSummaryModal);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));

  //* Close modal when change tab/strip
  useEffect(() => {
    if (!controlModal.isShowNewEvents && !controlModal.isShowAddNewEvent) {
      resetControlModal();
    }
  }, [selectedStrip]);

  //* Close modal when change tab
  useEffect(() => {
    if (!isActiveTab) {
      resetControlModal();
      resetControlHrSummaryModal();
    }
  }, [isActiveTab]);

  //* Pick date handler
  useEffect(() => {
    if (pickedStartDate && !pickedEndDate) {
      setPickDateAction(TPickDateAction.END);
    }
    if (pickedStartDate && pickedEndDate) {
      setPickDateAction(TPickDateAction.NONE);
    }
    if (!pickedStartDate && pickedEndDate) {
      setPickDateAction(TPickDateAction.NONE);
    }
  }, [pickedStartDate, pickedEndDate]);

  useEffect(() => {
    if (pickedBookmarkDate) {
      setPickDateAction(TPickDateAction.NONE);
    }
  }, [pickedBookmarkDate]);

  //* Change duration and Add new event modal have date picker => reset pick date state when close these modal
  useEffect(() => {
    if (Object.keys(controlModal).length > 1) {
      if (
        !(
          controlModal.isShowChangeDurationButtonType &&
          controlModal.isShowAddNewEvent &&
          controlModal.isShowAddEcgBookmark
        )
      ) {
        setPickedStartDate(null);
        setPickedEndDate(null);
        setPickedBookmarkDate(null);
        setPickDateAction(TPickDateAction.NONE);
      }
      if (controlModal.isShowChangeDurationButtonType || controlModal.isShowAddNewEvent) {
        setPickDateAction(TPickDateAction.START);
      }
      if (controlModal.isShowAddEcgBookmark) {
        setPickDateAction(TPickDateAction.BOOKMARK);
      }
    }
  }, [controlModal]);
};

export default useTabHRSummaryUIEffect;
